// Die Fahrt (B24, Erste Fassung, Runden 2 und 7): zwischen zwei Anlegestellen fährt das Boot etwa 10 Sekunden
// flussabwärts, man kann vorspulen. Es legt ab, fährt, bremst und legt mit dem Bug an der nächsten Anlegestelle an; dort
// wartet es, und man entscheidet. Nach dem Durchlass fährt es hinein, es wird kurz Nacht (T27), und am Morgen liegt es im
// nächsten Biom. Weg, Tageszeit und Wetter nach public/art/fahrt/fahrt-spiel.json des Grafik-Leads. Der Lauf hier ist
// reine Darstellung: Der Run ist schon weiter, die Fahrt holt ihn sichtbar ein.
import { biomeOfDay, runRules } from '../../content/index.ts';
import { stageNow, type RunFriend, type RunState } from '../../sim/index.ts';
import { drawFahrt, type Hit, type SceneState } from './scene.ts';
import { aboardOf } from './crew.ts';
import { loadFahrt, type Wetter } from './data.ts';
import { legDistance, legSeconds, reiseTempo, type Leg } from './path.ts';
import { onLightning, soundRunning } from '../sound.ts';
import { presentScene, sceneCanvas } from '../pixel.ts';

/** B24 (Fabi, 29.10.2026): So lange dauert eine Strecke bei normalem Tempo, in Sekunden (strecke.sekunden) */
export const STRECKE_SECONDS = 10;

type Travel = { from: number; to: number; startW: number; leg: Leg; tau: number };
/** Nacht zwischen zwei Tagen: einfahren, dunkel werden, ganz dunkel, hell werden; drive ist die Uhr der Durchfahrt */
type Night = { phase: 'ein' | 'dunkel' | 'halten' | 'hell'; t: number; drive: number; day: number; startW: number; leg: Leg };

const CALM: Wetter = { sturm: 0, dunkel: 0, nebel: 0, regen: 0, blitz: 0, wind: 0.2, blaetter: 0, wolken: 0, baenke: [0, 0, 0, 0], baenkeDunkel: [0, 0, 0, 0] };
/** Ohne Angabe schaukelt das Boot wie bisher nur sacht auf und ab */
const BOB = { hub: 1.2, neigung: 0, schub: 0, tempo: 1.4 };

const F = {
  /** Wo das Boot gerade gezeigt wird: Tag und Anlegestelle (-1: Morgen, vor der ersten) */
  day: 0,
  at: -1 as number,
  /** Strecken des gezeigten Tages (Anlegestellen + 1) */
  n: 4,
  /** Welches Boot gezeigt wird; es wächst erst über Nacht (B19) */
  boat: '',
  /** Weg seit dem Morgen, Bildpixel */
  w: 0,
  travel: null as Travel | null,
  night: null as Night | null,
  speed: 1,
  view: 'quer' as 'quer' | 'aussen',
  now: 0,
  /** Kräusel, wenn das Boot liegt */
  ripple: 0,
  /** Der Ausguck zeigt ans Ufer bis zu dieser Zeit */
  lookUntil: 0,
  hits: [] as Hit[],
  hover: null as string | null,
  /** Maus über dem Boot (Klang von drinnen in der Außenansicht) */
  hoverBoat: false,
  /** Runde 5: Blitz zum Donner, klingt in einer Viertelsekunde ab */
  flash: 0,
  /** Wetter: weich von einer Sturmstufe zur nächsten (sturmWechsel) */
  wetter: { stage: '', from: CALM, to: CALM, k: 1 },
  /** Runde 8: Phase des Schaukelns (Hinweis „Schaukeln des Boots“), läuft mit der Zeit, nie zurückgesetzt */
  sway: 0,
};

type Hooks = { arrived: () => void; openFriend: (id: string) => void };
let hooks: Hooks | null = null;
let current: RunState | null = null;

export function initFahrt(h: Hooks): void {
  hooks = h;
  onLightning(() => (F.flash = 1));
}

/** Ist das Boot gerade unterwegs (dann wartet die Entscheidung, bis es ankommt)? */
export function traveling(): boolean {
  return !!F.travel || !!F.night;
}

/** Wie weit die Strecke ist (0 bis 1), für die Anzeige */
export const travelShare = (): number => (F.travel ? Math.min(1, F.travel.tau / legSeconds(F.travel.leg)) : F.night ? 0 : 1);

/**
 * B25: Liegt das Boot noch an der letzten Anlegestelle (oder am Morgen am Ufer) und die nächste Schicht hat nicht
 * begonnen? Dann teilt man zu und legt ab
 */
export function needsDeparture(run: RunState): boolean {
  if (F.travel || F.night || run.pending.kind !== 'river') return false;
  const underway = run.shift ? run.shift.underway : true;
  return !underway && F.at !== run.landing && F.day === run.day;
}

/** Wo das Boot gerade gezeigt wird (Anlegestelle, -1 am Morgen) */
export const shownAt = (): number => F.at;

/** Vorspulen: erst schneller, beim zweiten Mal gleich ankommen */
export function fastForward(): void {
  if (!F.travel && !F.night) return;
  const fast = loadFahrt()?.spiel.spiel.vorspulen ?? 3;
  if (F.speed < fast) F.speed = fast;
  else {
    if (F.night) endNight(true);
    if (F.travel) F.travel.tau = legSeconds(F.travel.leg);
  }
}

export function toggleView(): 'quer' | 'aussen' {
  F.view = F.view === 'quer' ? 'aussen' : 'quer';
  return F.view;
}
export const viewMode = (): 'quer' | 'aussen' => F.view;

/** Klang von drinnen (Fabi, 29.10.2026): in der Innenansicht immer leise, von außen nur, solange die Maus über dem Boot ist */
export const insideAudible = (): boolean => F.view === 'quer' || F.hoverBoat;

const strecke = () => loadFahrt()?.spiel.spiel.strecke ?? { sekunden: STRECKE_SECONDS, weg: 680, ablegen: 1.5, anlegen: 2.5 };
/** Weg zur Anlegestelle i (-1: der Morgen, landings.length: der Durchlass) */
const wAt = (i: number) => (i + 1) * strecke().weg;

/**
 * Der Run hat sich geändert: Wo soll das Boot hin? Beim ersten Mal (Laden, neuer Run) liegt es gleich dort; danach fährt
 * es die Strecke sichtbar, über Nacht in den nächsten Tag.
 */
export function followRun(run: RunState): void {
  const fresh = current?.seed !== run.seed;
  current = run;
  const target = run.landing;
  if (fresh || F.day === 0) {
    F.day = run.day;
    F.n = run.landings.length + 1;
    // Ein neuer Run beginnt am Morgen und fährt zur ersten Anlegestelle; ein geladener liegt gleich dort
    F.at = run.day === 1 && run.landing === 0 && run.haltsToday === 0 && run.chronicle.length <= 2 ? -1 : target;
    F.w = wAt(F.at);
    F.travel = null;
    F.night = null;
    F.speed = 1;
    setWeather(stageNow(run), true);
  }
  if (run.day === F.day && !F.night) F.n = run.landings.length + 1;
  if (run.pending.kind !== 'river') return;
  if (F.night) return;
  // B25: Nach einem Halt und am Morgen wartet das Boot, bis abgelegt wird (Schichtwechsel)
  if (needsDeparture(run)) return;
  if (run.day !== F.day) {
    // T27: Nach dem Durchlass fährt das Boot hinein, es wird kurz dunkel, dann ist Morgen im nächsten Biom
    const s = loadFahrt()?.spiel.spiel;
    const v = reiseTempo(strecke()) * (s?.durchfahrt.tempo ?? 1.5);
    F.night = { phase: 'ein', t: 0, drive: 0, day: run.day, startW: F.w, leg: { L: 1e6, a: s?.durchfahrt.ablegen ?? 1.5, d: 0, v } };
    F.travel = null;
    return;
  }
  if (F.travel) {
    if (F.travel.to !== target) startTravel(F.w, F.at, target, false);
    return;
  }
  if (target !== F.at) startTravel(F.w, F.at, target, true);
}

/** Losfahren: von hier bis zur Anlegestelle `to`; über mehrere Strecken (eingeholt) entsprechend schneller */
function startTravel(fromW: number, from: number, to: number, atRest: boolean): void {
  const s = strecke();
  const L = Math.max(0, wAt(to) - fromW);
  const legs = Math.max(1, L / s.weg);
  F.travel = { from, to, startW: fromW, tau: 0, leg: { L, a: atRest ? s.ablegen : 0, d: s.anlegen, v: reiseTempo(s) * legs } };
  F.speed = 1;
}

function setWeather(stage: string, jump: boolean): void {
  const all = loadFahrt()?.spiel.sturmstufen;
  const to = all?.[stage];
  if (!to) return;
  if (jump || !F.wetter.stage) {
    F.wetter = { stage, from: to, to, k: 1 };
    return;
  }
  if (stage === F.wetter.stage) return;
  F.wetter = { stage, from: weatherNow(), to, k: 0 };
}

function weatherNow(): Wetter {
  const { from, to, k } = F.wetter;
  if (k >= 1) return to;
  const e = k * k * (3 - 2 * k);
  const mix = (a: number, b: number) => a + (b - a) * e;
  const opt = (a: number | undefined, b: number | undefined) => mix(a ?? 0, b ?? 0);
  const fs = from.schaukeln ?? BOB;
  const ts = to.schaukeln ?? BOB;
  return {
    sturm: mix(from.sturm, to.sturm),
    dunkel: mix(from.dunkel, to.dunkel),
    nebel: mix(from.nebel, to.nebel),
    regen: mix(from.regen, to.regen),
    blitz: k < 0.5 ? from.blitz : to.blitz,
    wind: mix(from.wind, to.wind),
    blaetter: mix(from.blaetter, to.blaetter),
    wolken: mix(from.wolken, to.wolken),
    baenke: to.baenke.map((b, i) => mix(from.baenke[i] ?? 0, b)),
    baenkeDunkel: to.baenkeDunkel.map((b, i) => mix(from.baenkeDunkel[i] ?? 0, b)),
    see: opt(from.see, to.see),
    rauheit: opt(from.rauheit, to.rauheit),
    dunst: opt(from.dunst, to.dunst),
    decke: opt(from.decke, to.decke),
    tief: opt(from.tief, to.tief),
    fern: opt(from.fern, to.fern),
    schraeg: opt(from.schraeg, to.schraeg),
    schaukeln: { hub: mix(fs.hub, ts.hub), neigung: mix(fs.neigung, ts.neigung), schub: mix(fs.schub, ts.schub), tempo: mix(fs.tempo, ts.tempo) },
  };
}

/** Mitten in der Nacht: der neue Tag beginnt am Morgen im nächsten Biom, ohne Überblenden */
function switchDay(run: RunState, day: number): void {
  F.day = day;
  F.at = -1;
  F.w = 0;
  F.n = run.landings.length + 1;
  setWeather(stageNow(run), true);
}

function endNight(skip: boolean): void {
  const run = current;
  const night = F.night;
  if (!night || !run) return;
  if (F.day !== night.day) switchDay(run, night.day);
  if (!skip && night.phase !== 'hell') return;
  F.night = null;
  F.speed = 1;
  if (run.pending.kind === 'river' && run.day === F.day && run.landing !== F.at && !needsDeparture(run)) startTravel(F.w, F.at, run.landing, true);
  else hooks?.arrived();
}

/** Ein Bild: Zeit weiterlaufen lassen und zeichnen */
export function fahrtFrame(dt: number): void {
  const canvas = document.getElementById('fahrt-canvas') as HTMLCanvasElement | null;
  const run = current;
  const all = loadFahrt();
  if (!canvas || !run || !all) return;
  const S = all.spiel.spiel;
  F.now += dt;
  // Wetter der Sturmstufe, weich übergeblendet; in der Nacht bleibt es, wie es war
  if (!F.night) setWeather(stageNow(run), false);
  F.wetter.k = Math.min(1, F.wetter.k + dt / Math.max(0.1, S.sturmWechsel));
  const wetter = weatherNow();
  // Runde 8: Das Boot schaukelt, mit der Sturmstufe stärker und schneller
  F.sway += dt * (all.spiel.schaukeln?.takt ?? 0.92) * (wetter.schaukeln ?? BOB).tempo;
  let night = 0;
  if (F.night) {
    const N = F.night;
    const step = dt * F.speed;
    // Das Boot fährt in den Durchlass hinein und weiter, bis es ganz dunkel ist
    if (N.phase === 'ein' || N.phase === 'dunkel') {
      N.drive += step;
      F.w = N.startW + legDistance(N.leg, N.drive);
    }
    if (N.phase === 'ein') {
      N.t += step;
      if (F.w - F.n * strecke().weg >= S.durchfahrt.nachtAb || N.t > 4) {
        N.phase = 'dunkel';
        N.t = 0;
      }
    } else if (N.phase === 'dunkel') {
      N.t += step;
      night = smoothstep(N.t / all.d.takt.nacht);
      if (N.t >= all.d.takt.nacht) {
        N.phase = 'halten';
        N.t = 0;
      }
    } else if (N.phase === 'halten') {
      N.t += step;
      night = 1;
      if (N.t >= S.nacht.halten) {
        switchDay(run, N.day);
        N.phase = 'hell';
        N.t = 0;
      }
    }
    if (N.phase === 'hell') {
      N.t += step;
      // Es wird hell, etwa so lange, wie die Tageszeit von 0 bis aufhellen braucht
      const len = S.nacht.aufhellen + 0.2;
      night = 1 - smoothstep(N.t / len);
      if (N.t >= len) endNight(false);
    }
  }
  if (F.travel) {
    const tr = F.travel;
    tr.tau += dt * F.speed;
    const D = legSeconds(tr.leg);
    F.w = tr.startW + legDistance(tr.leg, tr.tau);
    if (tr.tau >= D) {
      F.at = tr.to;
      F.w = wAt(tr.to);
      F.travel = null;
      F.speed = 1;
      // Der Ausguck zeigt ans Ufer: Da ist die nächste Anlegestelle
      F.lookUntil = F.now + 2.4;
      hooks?.arrived();
    }
  } else if (!F.night) F.ripple += dt * S.liegen.kraeusel;
  if (!F.boat || !F.night || F.night.phase === 'hell') F.boat = boatArt(run);
  const art = F.boat;
  const boat = all.boote.boote[art] ?? Object.values(all.boote.boote)[0]!;
  const share = F.travel ? F.travel.tau / legSeconds(F.travel.leg) : 0;
  const state: SceneState = {
    biome: biomeOfDay(F.day || run.day).id,
    w: F.w,
    n: F.n,
    wetter: { ...wetter, schaukeln: wetter.schaukeln ?? BOB },
    now: F.now,
    sway: F.sway,
    ripple: F.ripple,
    boat: art,
    view: F.view,
    crew: aboardOf(run, boat),
    // Unterwegs quatschen die im Gemeinschaftsraum; kommt das Boot an, ruft der Ausguck
    chat: !!F.travel && share > 0.12 && share < 0.88,
    look: !F.travel && F.now < F.lookUntil,
    night,
  };
  F.flash = Math.max(0, F.flash - dt * 4);
  if (soundRunning()) state.flash = F.flash;
  F.hits = [];
  // Runde 7: in Bildgröße malen, dann ganzzahlig vergrößert auf die Leinwand (nichts verzerrt)
  const scene = sceneCanvas('fahrt', all.d.breite, all.d.hoehe);
  if (drawFahrt(scene, state, F.hits)) presentScene(canvas, scene);
  const tag = document.getElementById('fahrt-tag');
  if (tag) tag.textContent = F.hover ? (run.crew.find((c) => c.id === F.hover)?.name ?? '') : '';
  const eta = document.querySelector<HTMLElement>('.travel-card .eta i');
  if (eta) eta.style.width = `${Math.round(travelShare() * 100)}%`;
}

const smoothstep = (x: number) => {
  const t = Math.max(0, Math.min(1, x));
  return t * t * (3 - 2 * t);
};

/**
 * B19 (Fabi): Das Boot wächst wie ein echtes Boot. Erster Tag Floß, nach dem ersten Durchlass das Floß mit Hütte, am
 * dritten Tag das Hausboot (boot/boote.json)
 */
export function boatArt(run: Pick<RunState, 'crewSlots'>): string {
  const b = loadFahrt()?.boote.boote;
  const stage = Math.max(0, run.crewSlots - runRules.crewSlots.start);
  const order = ['floss', 'huette', 'hausboot'].filter((id) => !b || b[id]);
  return order[Math.min(order.length - 1, stage)] ?? 'floss';
}

/** Maus über dem Bild: wer ist das? Klick öffnet seine Karte */
export function bindFahrtCanvas(canvas: HTMLCanvasElement): void {
  const at = (ev: MouseEvent) => {
    const r = canvas.getBoundingClientRect();
    const d = loadFahrt()?.d;
    const x = ((ev.clientX - r.left) / r.width) * (d?.breite ?? 640);
    const y = ((ev.clientY - r.top) / r.height) * (d?.hoehe ?? 360);
    const run = current;
    const boat = run ? loadFahrt()?.boote.boote[F.boat || boatArt(run)] : undefined;
    F.hoverBoat = !!boat && x >= boat.fahrt[0] && x < boat.fahrt[0] + boat.w && y >= boat.fahrt[1] && y < boat.fahrt[1] + boat.h;
    return [...F.hits].reverse().find((h) => x >= h.x && x < h.x + h.w && y >= h.y && y < h.y + h.h)?.key ?? null;
  };
  canvas.onmousemove = (ev) => {
    F.hover = at(ev);
    canvas.style.cursor = F.hover ? 'pointer' : 'default';
  };
  canvas.onmouseleave = () => {
    F.hover = null;
    F.hoverBoat = false;
  };
  canvas.onclick = (ev) => {
    const key = at(ev);
    if (key) hooks?.openFriend(key);
    else if (traveling()) fastForward();
  };
}

export type { RunFriend };

/** Zum Prüfen im Browser (Playwright): wo das Boot gerade gezeigt wird */
export function fahrtDebug(): { day: number; at: number; w: number; n: number; travel: Travel | null; night: string | null; view: string; boat: string } {
  return { day: F.day, at: F.at, w: F.w, n: F.n, travel: F.travel, night: F.night?.phase ?? null, view: F.view, boat: current ? boatArt(current) : '' };
}
